// src/components/SetupWizard/WizardStep2_Financials.js
//
// Phase 3, Build Step 3: Financial Context — REAL IMPLEMENTATION
//
// Four paths: QuickBooks OAuth + fetch, PDF upload, Financial Ranges estimates, or Skip.
//
// QuickBooks flow inside wizard:
//   1. Connect (OAuth redirect — saves wizard state to sessionStorage)
//   2. Return from QB → wizard auto-reopens, shows "Connected" chip
//   3. Select most recently closed month/year
//   4. Fetch QuickBooks Data (calls WorkspaceQuickBooksFinancials)
//   5. On success → "Continue →" enabled
//
// Props:
//   clientId       — string, the client being set up
//   clientData     — client document from Firestore (real-time)
//   user           — Firebase auth user object
//   onStepComplete — callback() when step is done (any path)
//   onBack         — callback to go back to Step 1

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { toast } from 'react-toastify';
import ManualFinancialUpload from '../ManualFinancialUpload';

// Month options for the period selector
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

// Get previous month as default (most likely the last closed period)
const getPreviousMonthAndYear = () => {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth(); // 0-indexed, so Jan = 0
  if (month === 0) {
    month = 12;
    year -= 1;
  }
  return { defaultYear: year, defaultMonth: month };
};

// Financial range options
const REVENUE_RANGES = ['<$500K', '$500K–$1M', '$1M–$5M', '$5M–$15M', '$15M–$50M', '$50M+'];
const REVENUE_TRAJECTORIES = ['Growing >20%', 'Growing <20%', 'Flat', 'Declining'];
const PROFITABILITY_STATUSES = ['Profitable', 'Breakeven', 'Burning Cash'];
const CASH_RUNWAYS = ['Comfortable 12+ months', '6–12 months', '<6 months', 'N/A – Profitable'];
const REVENUE_MODELS = ['Recurring/Subscription', 'Project-Based', 'Product Sales', 'Mixed'];
const GROSS_MARGIN_RANGES = ['<30%', '30–50%', '50–70%', '70–85%', '>85%'];
const HEADCOUNT_RANGES = ['1–10', '11–50', '51–200', '201–500', '501–1000', '>1000'];
const FUNDING_STAGES = ['Bootstrapped', 'Pre-seed/Seed', 'Series A', 'Series B+', 'Private Equity owned', 'N/A'];

const WizardStep2_Financials = ({ clientId, clientData, user, onStepComplete, onBack }) => {
  const [isConnectingToQB, setIsConnectingToQB] = useState(false);
  const [isFetchingQBData, setIsFetchingQBData] = useState(false);
  const [qbFetchComplete, setQbFetchComplete] = useState(false);
  const [qbFetchMessage, setQbFetchMessage] = useState({ type: '', text: '' });
  const [isSkipping, setIsSkipping] = useState(false);
  const [pdfUploadComplete, setPdfUploadComplete] = useState(false);

  // Financial ranges state
  const [rangesExpanded, setRangesExpanded] = useState(false);
  const [revenueRange, setRevenueRange] = useState('');
  const [revenueTrajectory, setRevenueTrajectory] = useState('');
  const [profitabilityStatus, setProfitabilityStatus] = useState('');
  const [cashRunway, setCashRunway] = useState('');
  const [revenueModel, setRevenueModel] = useState('');
  const [grossMarginRange, setGrossMarginRange] = useState('');
  const [headcountRange, setHeadcountRange] = useState('');
  const [fundingStage, setFundingStage] = useState('');
  const [isSubmittingRanges, setIsSubmittingRanges] = useState(false);

  // Month/year selector for QB data fetch
  const { defaultYear, defaultMonth } = getPreviousMonthAndYear();
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);

  // Year options: 3 years back + 1 year forward
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i);

  // Derive status from real-time clientData
  const qbConnected = !!clientData?.quickbooksConnected;
  const qbCompanyName = clientData?.quickbooksCompanyName || null;
  const qbRealmId = clientData?.quickbooksRealmId || null;
  const financialsUploaded = !!clientData?.financialsUploaded;
  const financialsSkipped = !!clientData?.financialsSkipped;
  const financialRangesProvided = !!clientData?.financialRangesProvided;

  // Only show abbreviated "already complete" view after full setup is done.
  // During active setup, always show the full form so user can change their mind.
  const isAlreadyComplete = (financialsUploaded || financialsSkipped || financialRangesProvided)
    && !!clientData?.isSetupComplete;

  // Track QB "just connected" state for wizard flow
  const [qbJustConnected, setQbJustConnected] = useState(false);

  useEffect(() => {
    // Detect QB connection happening while wizard is open (after OAuth return)
    if (qbConnected && !qbJustConnected) {
      const resumeFlag = sessionStorage.getItem('wizardResumeAfterQB');
      if (resumeFlag) {
        sessionStorage.removeItem('wizardResumeAfterQB');
        setQbJustConnected(true);
        toast.success(`QuickBooks connected: ${qbCompanyName || 'Company'}`);
      }
    }
  }, [qbConnected, qbCompanyName, qbJustConnected]);

  // Also detect if QB was already connected before wizard opened (e.g., connected from Manage Clients)
  useEffect(() => {
    if (qbConnected && !qbJustConnected && !financialsUploaded) {
      setQbJustConnected(true);
    }
  }, []); // Run once on mount

  // ----- QuickBooks OAuth -----
  const handleConnectQuickBooks = async () => {
    if (!clientId) {
      toast.error('No client selected. Cannot connect to QuickBooks.');
      return;
    }

    setIsConnectingToQB(true);

    try {
      const initiateAuth = httpsCallable(functions, 'initiateQuickBooksAuth');
      const result = await initiateAuth({ forClientId: clientId, inSetupFlow: true });

      if (result.data?.success && result.data?.authUrl) {
        // Save wizard state before the full-page redirect
        // ClientDashboard will read this on return to auto-reopen the wizard
        sessionStorage.setItem('wizardResumeClientId', clientId);
        sessionStorage.setItem('wizardResumeAfterQB', 'true');

        // Full page redirect to QuickBooks OAuth
        window.location.href = result.data.authUrl;
        // Execution stops here — page navigates away
      } else {
        const errMsg = result.data?.message || 'Could not retrieve authorization URL.';
        toast.error(`Connection failed: ${errMsg}`);
      }
    } catch (error) {
      const errMsg = error.message || 'An unknown error occurred.';
      toast.error(`Error initiating QuickBooks connection: ${errMsg}`);
    } finally {
      setIsConnectingToQB(false);
    }
  };

  // ----- QuickBooks Data Fetch -----
  const handleFetchQuickBooksData = async () => {
    if (!qbRealmId || !clientId) {
      toast.error('Cannot fetch data: Missing Realm ID or Client ID.');
      return;
    }
    if (!selectedYear || !selectedMonth) {
      toast.error('Please select a month and year.');
      return;
    }

    const lastDayOfMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const userSpecifiedPeriodEndStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

    setIsFetchingQBData(true);
    setQbFetchMessage({ type: '', text: '' });
    const toastId = toast.loading(`Fetching QuickBooks data up to ${selectedMonth}/${selectedYear}...`);

    try {
      const fetchQBDataFunction = httpsCallable(functions, 'WorkspaceQuickBooksFinancials');
      const result = await fetchQBDataFunction({
        clientId: clientId,
        userSpecifiedPeriodEnd: userSpecifiedPeriodEndStr
      });

      if (result.data?.success) {
        const missingReports = Array.isArray(result.data?.missingReports) ? result.data.missingReports : [];
        const baseMsg = result.data.message || 'QuickBooks data fetched successfully.';
        const suffix = missingReports.length ? ` (partial: missing ${missingReports.join(', ')})` : '';
        const msg = `${baseMsg}${suffix}`;

        setQbFetchMessage({ type: 'success', text: msg });
        setQbFetchComplete(true);
        toast.update(toastId, { render: msg, type: 'success', isLoading: false, autoClose: 5000 });
      } else {
        const errorMsg = result.data?.message || 'Failed to fetch data from QuickBooks.';
        setQbFetchMessage({ type: 'error', text: errorMsg });
        toast.update(toastId, { render: errorMsg, type: 'error', isLoading: false, autoClose: 5000 });
      }
    } catch (error) {
      let errorMsg = error.message || 'An unknown error occurred while fetching data.';
      if (error.details && typeof error.details === 'object' && error.details.message) {
        errorMsg = error.details.message;
      } else if (typeof error.details === 'string') {
        errorMsg = error.details;
      }
      setQbFetchMessage({ type: 'error', text: `Error: ${errorMsg}` });
      toast.update(toastId, { render: errorMsg, type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setIsFetchingQBData(false);
    }
  };

  // ----- PDF Upload Complete -----
  const handleFinancialUploadComplete = useCallback(() => {
    console.log('[WizardStep2] ManualFinancialUpload signaled completion');
    setPdfUploadComplete(true);

    // Set financialsUploaded flag on client doc
    const clientRef = doc(db, 'clients', clientId);
    updateDoc(clientRef, {
      financialsUploaded: true,
      updatedAt: serverTimestamp()
    }).catch(err => {
      console.error('[WizardStep2] Error setting financialsUploaded:', err);
    });

    toast.success('Financial document uploaded successfully!');
  }, [clientId]);

  // ----- Submit Financial Ranges -----
  const handleSubmitRanges = async () => {
    if (!revenueRange) {
      toast.error('Please select at least an annual revenue range.');
      return;
    }

    setIsSubmittingRanges(true);
    try {
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, {
        estimatedRevenueRange: revenueRange,
        estimatedRevenueTrajectory: revenueTrajectory || null,
        estimatedProfitabilityStatus: profitabilityStatus || null,
        estimatedCashRunway: cashRunway || null,
        estimatedRevenueModel: revenueModel || null,
        estimatedGrossMarginRange: grossMarginRange || null,
        estimatedHeadcountRange: headcountRange || null,
        estimatedFundingStage: fundingStage || null,
        financialRangesProvided: true,
        financialRangesUpdatedAt: serverTimestamp(),
        financialsSkipped: false,
        financialsUploaded: false,
        financialIntelligenceTier: 'ranges_only',
        updatedAt: serverTimestamp()
      });
      toast.success('Financial estimates saved successfully!');
      onStepComplete();
    } catch (err) {
      console.error('[WizardStep2] Ranges submit error:', err);
      toast.error('Failed to save estimates. Please try again.');
    } finally {
      setIsSubmittingRanges(false);
    }
  };

  // ----- Skip -----
  const handleSkip = async () => {
    setIsSkipping(true);
    try {
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, {
        financialsSkipped: true,
        financialIntelligenceTier: 'questionnaire_only',
        updatedAt: serverTimestamp()
      });
      toast.info('Financials skipped — you can add them later from the Manage Clients tab.');
      onStepComplete();
    } catch (err) {
      console.error('[WizardStep2] Skip error:', err);
      toast.error('Failed to skip. Please try again.');
    } finally {
      setIsSkipping(false);
    }
  };

  // ----- Continue -----
  const handleContinue = () => {
    onStepComplete();
  };

  // ----- Already Complete State -----
  if (isAlreadyComplete) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Financial Context
        </Typography>

        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          Financial data already provided for this client.
          {qbConnected && financialsUploaded && ` QuickBooks connected (${qbCompanyName || 'connected'}) and data imported.`}
          {financialsUploaded && !qbConnected && ' Financial documents uploaded.'}
          {financialRangesProvided && !financialsUploaded && ' Financial estimates provided (ranges only).'}
          {financialsSkipped && !financialsUploaded && !financialRangesProvided && ' Financials skipped — questionnaire-only mode.'}
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={onBack} sx={{ textTransform: 'none' }}>
            ← Back
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{ minWidth: 180 }}
          >
            Continue →
          </Button>
        </Box>
      </Box>
    );
  }

  // ----- Determine if Continue should be enabled -----
  const showContinueButton = qbFetchComplete || pdfUploadComplete || financialsUploaded || financialRangesProvided;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Financial Context
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect financial data to enable comprehensive analysis. Choose one option below, or skip and add financials later.
      </Typography>

      {/* Two-column layout: QuickBooks | PDF Upload */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>

        {/* Left Column: QuickBooks */}
        <Paper variant="outlined" sx={{ flex: 1, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LinkIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              Connect QuickBooks
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Securely import up to 3 years of financial reports automatically via QuickBooks Online.
          </Typography>

          {/* State 1: Not connected — show Connect button */}
          {!qbConnected && !qbJustConnected && (
            <Button
              variant="outlined"
              onClick={handleConnectQuickBooks}
              disabled={isConnectingToQB}
              sx={{ textTransform: 'none' }}
            >
              {isConnectingToQB ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                  Connecting...
                </>
              ) : (
                'Connect QuickBooks'
              )}
            </Button>
          )}

          {/* State 2: Connected — show period selector + fetch button */}
          {(qbConnected || qbJustConnected) && !qbFetchComplete && (
            <Box>
              <Chip
                icon={<CheckCircleIcon />}
                label={`Connected${qbCompanyName ? `: ${qbCompanyName}` : ''}`}
                color="success"
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the most recently closed month and year, then fetch your financial data.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Closed Month</InputLabel>
                    <Select
                      value={selectedMonth}
                      label="Closed Month"
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      disabled={isFetchingQBData}
                    >
                      {MONTHS.map(m => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Closed Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Closed Year"
                      onChange={(e) => setSelectedYear(e.target.value)}
                      disabled={isFetchingQBData}
                    >
                      {years.map(y => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="success"
                onClick={handleFetchQuickBooksData}
                disabled={isFetchingQBData}
                startIcon={isFetchingQBData ? <CircularProgress size={18} /> : <DownloadIcon />}
                sx={{ textTransform: 'none' }}
              >
                {isFetchingQBData ? 'Fetching & Saving...' : 'Fetch QuickBooks Data'}
              </Button>

              {qbFetchMessage.text && (
                <Alert
                  severity={qbFetchMessage.type === 'error' ? 'error' : 'success'}
                  sx={{ mt: 2 }}
                >
                  {qbFetchMessage.text}
                </Alert>
              )}
            </Box>
          )}

          {/* State 3: Data fetched — show success */}
          {qbFetchComplete && (
            <Box>
              <Chip
                icon={<CheckCircleIcon />}
                label={`Connected${qbCompanyName ? `: ${qbCompanyName}` : ''}`}
                color="success"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              <Alert severity="success" sx={{ mt: 1 }}>
                QuickBooks financial data imported successfully. Click Continue to proceed.
              </Alert>
            </Box>
          )}
        </Paper>

        {/* Right Column: PDF Upload */}
        <Paper variant="outlined" sx={{ flex: 1, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <UploadFileIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              Upload Financial PDF
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload your client's financial statements as a single PDF. For best results, use an
            electronically-generated PDF (not a scan) that includes a Profit & Loss Statement,
            Balance Sheet, and Cash Flow Statement. Max file size: 25 MB.
          </Typography>

          {pdfUploadComplete ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Document uploaded successfully"
              color="success"
              variant="outlined"
            />
          ) : (
            <ManualFinancialUpload
              clientId={clientId}
              user={user}
              selectedClient={clientData}
              clientIsActive={false}
              onUploadComplete={handleFinancialUploadComplete}
            />
          )}
        </Paper>
      </Box>

      {/* Financial Ranges — Third Option */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Button
          onClick={() => setRangesExpanded(!rangesExpanded)}
          endIcon={rangesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          startIcon={<TuneIcon />}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            fontSize: '0.9rem',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          Don't have financials ready? Provide estimates instead
        </Button>

        <Collapse in={rangesExpanded}>
          <Paper variant="outlined" sx={{ p: 3, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Financial context helps SavvaiEdge size strategic recommendations to your client's
              actual situation. Select approximate ranges below — only Annual Revenue is required.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              You can connect QuickBooks or upload detailed financials at any time from the
              Manage Clients tab for more precise insights.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Annual Revenue Range *</InputLabel>
                <Select
                  value={revenueRange}
                  label="Annual Revenue Range *"
                  onChange={(e) => setRevenueRange(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {REVENUE_RANGES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Revenue Trajectory</InputLabel>
                <Select
                  value={revenueTrajectory}
                  label="Revenue Trajectory"
                  onChange={(e) => setRevenueTrajectory(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {REVENUE_TRAJECTORIES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Profitability Status</InputLabel>
                <Select
                  value={profitabilityStatus}
                  label="Profitability Status"
                  onChange={(e) => setProfitabilityStatus(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {PROFITABILITY_STATUSES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Cash Runway</InputLabel>
                <Select
                  value={cashRunway}
                  label="Cash Runway"
                  onChange={(e) => setCashRunway(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {CASH_RUNWAYS.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Primary Revenue Model</InputLabel>
                <Select
                  value={revenueModel}
                  label="Primary Revenue Model"
                  onChange={(e) => setRevenueModel(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {REVENUE_MODELS.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Gross Margin Range</InputLabel>
                <Select
                  value={grossMarginRange}
                  label="Gross Margin Range"
                  onChange={(e) => setGrossMarginRange(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {GROSS_MARGIN_RANGES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Headcount Range</InputLabel>
                <Select
                  value={headcountRange}
                  label="Headcount Range"
                  onChange={(e) => setHeadcountRange(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {HEADCOUNT_RANGES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Funding Stage</InputLabel>
                <Select
                  value={fundingStage}
                  label="Funding Stage"
                  onChange={(e) => setFundingStage(e.target.value)}
                  disabled={isSubmittingRanges}
                >
                  {FUNDING_STAGES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {revenueRange && (
              <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSubmitRanges}
                  disabled={isSubmittingRanges}
                  sx={{ textTransform: 'none' }}
                >
                  {isSubmittingRanges ? (
                    <>
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Continue with Estimates'
                  )}
                </Button>
              </Box>
            )}
          </Paper>
        </Collapse>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button onClick={onBack} sx={{ textTransform: 'none' }}>
          ← Back
        </Button>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!showContinueButton && (
            <Button
              onClick={handleSkip}
              disabled={isSkipping}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.85rem' }}
            >
              {isSkipping ? 'Skipping...' : 'Skip for Now — add financials later'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!showContinueButton}
            sx={{ minWidth: 180 }}
          >
            Continue →
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WizardStep2_Financials;